import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import heic2any from 'heic2any';
import {
  Box,
  Typography,
  Button,
  Card,
  Grid,
  LinearProgress,
  IconButton,
  TextField,
  Chip,
  Alert,
  Snackbar,
  Divider,
  Paper,
  Skeleton,
  Tooltip,
  InputAdornment,
  TablePagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  PhotoLibrary,
  Photo as PhotoIcon,
  CheckCircle,
  ArrowForward,
  Search,
  Clear,
  ZoomIn,
  BrokenImage,
  ExpandMore,
  ErrorOutline,
  Warning,
} from '@mui/icons-material';
import imageService from '../../api/imageService';
import sessionService from '../../api/sessionService';
import GooglePhotosDialog from '../GooglePhotosDialog';
import AuthenticatedImage from '../AuthenticatedImage';

const logger = {
  info: (...a) => console.log('[WizardStep1]', ...a),
  warn: (...a) => console.warn('[WizardStep1]', ...a),
  error: (...a) => console.error('[WizardStep1]', ...a),
};

const MAX_PREVIEWS_PER_SELECTION = 40;

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const generateHeicPreview = async (file) => {
  try {
    const convertedBlob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.7 });
    return URL.createObjectURL(convertedBlob);
  } catch {
    return null;
  }
};

/**
 * WizardStep1Upload
 * Props:
 *   session        – current session object
 *   onNext         – callback when step is completed (passes { uploadedCount })
 *   sessionPhotoCount – total photos already in session
 *   onSessionRefresh  – callback to refresh session stats
 */
const WizardStep1Upload = ({ session, onNext, sessionPhotoCount = 0, onSessionRefresh, onNavConfigChange }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewFiles, setPreviewFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [googlePhotosDialog, setGooglePhotosDialog] = useState({ open: false });
  const [totalUploaded, setTotalUploaded] = useState(0);
  const [importResult, setImportResult] = useState(null); // { uploadedFiles, errors, skipped, total }

  // ── Gallery state ────────────────────────────────────────────────────────────
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryPage, setGalleryPage] = useState(0);          // 0-based for MUI
  const [galleryRowsPerPage, setGalleryRowsPerPage] = useState(24);
  const [galleryTotal, setGalleryTotal] = useState(0);
  const [gallerySearch, setGallerySearch] = useState('');
  const searchTimeoutRef = useRef(null);

  const showSnackbar = (message, severity = 'info') =>
    setSnackbar({ open: true, message, severity });

  // ── Load gallery images from API ─────────────────────────────────────────────
  const loadGalleryImages = useCallback(async (page = 0, rowsPerPage = 24, search = '') => {
    if (!session?.uniqueId) return;
    setGalleryLoading(true);
    try {
      const response = await sessionService.getSessionImages(session.uniqueId, {
        page: page + 1,          // API is 1-based
        limit: rowsPerPage,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        search: search || undefined,
        mediaType: 'image',
      });
      setGalleryImages(response.images || []);
      setGalleryTotal(response.pagination?.totalCount || 0);
    } catch (err) {
      console.error('[WizardStep1] Failed to load gallery', err);
    } finally {
      setGalleryLoading(false);
    }
  }, [session?.uniqueId]);

  // Initial load and reload whenever page / rowsPerPage change
  useEffect(() => {
    loadGalleryImages(galleryPage, galleryRowsPerPage, gallerySearch);
  }, [galleryPage, galleryRowsPerPage, loadGalleryImages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  const handleGallerySearchChange = (value) => {
    setGallerySearch(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setGalleryPage(0);
      loadGalleryImages(0, galleryRowsPerPage, value);
    }, 400);
  };

  // ─── Dropzone ────────────────────────────────────────────────────────────────
  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      const errors = rejectedFiles
        .map((f) => `${f.file.name}: ${f.errors.map((e) => e.message).join(', ')}`)
        .join('; ');
      showSnackbar(`Some files were rejected: ${errors}`, 'warning');
    }
    if (acceptedFiles.length === 0) return;

    logger.info('Queueing local files for upload', {
      fileCount: acceptedFiles.length,
      previewLimit: MAX_PREVIEWS_PER_SELECTION,
    });

    setSelectedFiles((prev) => [...prev, ...acceptedFiles]);

    const previewStartIndex = previewFiles.length;
    const filesToPreview = acceptedFiles.slice(0, MAX_PREVIEWS_PER_SELECTION);
    const skippedPreviewCount = acceptedFiles.length - filesToPreview.length;

    const queuedPreviews = acceptedFiles.map((file, index) => {
      const isHeic =
        file.type === 'image/heic' ||
        file.type === 'image/heif' ||
        /\.(heic|heif|eic)$/i.test(file.name);

      return {
        file,
        preview: null,
        name: file.name,
        description: '',
        tags: '',
        isHeic,
        size: file.size,
        previewDeferred: index >= MAX_PREVIEWS_PER_SELECTION,
      };
    });

    setPreviewFiles((prev) => [...prev, ...queuedPreviews]);

    if (skippedPreviewCount > 0) {
      showSnackbar(
        `Queued ${acceptedFiles.length} files. Generated previews for the first ${filesToPreview.length}; ${skippedPreviewCount} more will upload without previews.`,
        'info'
      );
    }

    const generatedPreviews = await Promise.all(
      filesToPreview.map(async (file) => {
        const isHeic =
          file.type === 'image/heic' ||
          file.type === 'image/heif' ||
          /\.(heic|heif|eic)$/i.test(file.name);
        const preview = isHeic ? await generateHeicPreview(file) : URL.createObjectURL(file);
        return { preview, isHeic };
      })
    );

    setPreviewFiles((prev) => {
      const next = [...prev];
      generatedPreviews.forEach((generatedPreview, index) => {
        const targetIndex = previewStartIndex + index;
        if (!next[targetIndex]) return;
        next[targetIndex] = {
          ...next[targetIndex],
          preview: generatedPreview.preview,
          previewDeferred: false,
        };
      });
      return next;
    });
  }, [previewFiles.length]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.bmp', '.tiff', '.heic', '.heif', '.eic'],
    },
    maxSize: 50 * 1024 * 1024,
    multiple: true,
  });

  // ─── File list helpers ────────────────────────────────────────────────────────
  const removeFile = (index) => {
    const newFiles = [...selectedFiles];
    const newPreviews = [...previewFiles];
    if (newPreviews[index].preview) URL.revokeObjectURL(newPreviews[index].preview);
    newFiles.splice(index, 1);
    newPreviews.splice(index, 1);
    setSelectedFiles(newFiles);
    setPreviewFiles(newPreviews);
  };

  const updateMeta = (index, field, value) => {
    setPreviewFiles((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const clearAll = () => {
    previewFiles.forEach((p) => { if (p.preview) URL.revokeObjectURL(p.preview); });
    setSelectedFiles([]);
    setPreviewFiles([]);
  };

  // ─── Upload ───────────────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      showSnackbar('Please select at least one image to upload', 'warning');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const metadata = {
        sessionId: session.uniqueId,
        names: previewFiles.map((p) => p.name),
        descriptions: previewFiles.map((p) => p.description),
        tags: previewFiles.map((p) => p.tags),
      };

      const result = await imageService.uploadImages(selectedFiles, metadata, (pct) =>
        setUploadProgress(pct)
      );

      logger.info('Upload completed', result);

      const newCount = result.uploaded || 0;
      setTotalUploaded((prev) => prev + newCount);
      clearAll();
      setUploadProgress(0);

      if (result.failed > 0) {
        showSnackbar(
          `Upload completed: ${result.uploaded} successful, ${result.failed} failed`,
          'warning'
        );
      } else {
        showSnackbar(`Successfully uploaded ${result.uploaded} images`, 'success');
      }

      if (onSessionRefresh) await onSessionRefresh();
      // Reload gallery to show the newly uploaded images
      await loadGalleryImages(0, galleryRowsPerPage, gallerySearch);
      setGalleryPage(0);
    } catch (error) {
      logger.error('Upload failed', { error: error.message });
      showSnackbar('Upload failed: ' + error.message, 'error');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // ─── Google Photos ────────────────────────────────────────────────────────────
  const handleGooglePhotosImported = async (result) => {
    // result may be { uploadedFiles, errors, skipped, total } (new) or an array (legacy)
    const uploadedFiles = Array.isArray(result) ? result : (result?.uploadedFiles || []);
    const errors = Array.isArray(result) ? [] : (result?.errors || []);
    const skipped = Array.isArray(result) ? [] : (result?.skipped || []);
    const total = Array.isArray(result) ? uploadedFiles.length : (result?.total || uploadedFiles.length);

    const count = uploadedFiles.length;
    setTotalUploaded((prev) => prev + count);
    setImportResult({ uploadedFiles, errors, skipped, total });
    showSnackbar(
      errors.length > 0 || skipped.length > 0
        ? `Imported ${count} of ${total} photos. ${errors.length} failed, ${skipped.length} skipped.`
        : `Successfully imported ${count} photos from Google Photos!`,
      errors.length > 0 ? 'warning' : 'success'
    );
    setGooglePhotosDialog({ open: false });
    if (onSessionRefresh) await onSessionRefresh();
    // Reload gallery to show the newly imported images
    await loadGalleryImages(0, galleryRowsPerPage, gallerySearch);
    setGalleryPage(0);
  };

  // ─── Computed state ────────────────────────────────────────────────────────────
  const currentTotal = sessionPhotoCount + totalUploaded;
  const canContinue = currentTotal > 0;

  // True when another browser tab is currently uploading to this session
  const isUploadInProgressElsewhere = (() => {
    const uStats = session?.stepStatus?.upload;
    if (uStats?.inProgress !== true) return false;
    const startedAt = uStats.inProgressStartedAt ? new Date(uStats.inProgressStartedAt) : null;
    return !startedAt || (Date.now() - startedAt.getTime()) < 30 * 60 * 1000;
  })();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    onNavConfigChange?.({
      nextLabel: 'Continue',
      nextDisabled: !canContinue || uploading,
      backDisabled: false,
      showSkip: false,
    });
  }, [canContinue, uploading]); // onNavConfigChange is stable

  return (
    <Box>
      {/* ── Instructions ────────────────────────────────────────────────────────── */}
      <Box sx={{ px: 2, py: 1.5, mb: 2.5, bgcolor: 'grey.50', borderLeft: '3px solid', borderColor: 'primary.main', borderRadius: '0 8px 8px 0' }}>
        <Typography variant="body2" color="text.secondary">
          Add photos to your session from your local device or from Google Photos. You can upload
          batches one at a time. When you're done, click <strong>Continue</strong> to proceed to
          face recognition.
        </Typography>
      </Box>

      {/* ── Info banner ─────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Upload Photos
        </Typography>
        <Chip
          icon={<PhotoLibrary />}
          label={`${currentTotal} photo${currentTotal !== 1 ? 's' : ''} in session`}
          color={currentTotal > 0 ? 'primary' : 'default'}
          variant={currentTotal > 0 ? 'filled' : 'outlined'}
        />
        {totalUploaded > 0 && (
          <Chip
            icon={<CheckCircle />}
            label={`${totalUploaded} uploaded this session`}
            color="success"
            variant="outlined"
          />
        )}
      </Box>

      {isUploadInProgressElsewhere && (
        <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
          <strong>Upload in progress in another tab.</strong> An image upload is already running for this session in another browser tab.
          Please wait for it to finish before starting a new upload.
        </Alert>
      )}

      {/* ── Upload ──────────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5, lineHeight: 1, whiteSpace: 'nowrap' }}>Upload</Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
      </Box>

      <Grid container spacing={3}>
        {/* ── Dropzone ──────────────────────────────────────────────────────────── */}
        <Grid item xs={12} md={6}>
          <Paper
            {...getRootProps()}
            elevation={0}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 5,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: isDragActive ? 'action.hover' : 'background.paper',
              transition: 'all 0.2s ease',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              minHeight: 200,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <input {...getInputProps()} />
            <PhotoLibrary sx={{ fontSize: 56, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {isDragActive ? 'Drop images here…' : 'Drag & drop images, or click to select'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              JPEG · PNG · GIF · WebP · BMP · TIFF · HEIC (max 50 MB each)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
              HEIC files are automatically converted to JPEG
            </Typography>
          </Paper>

          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<PhotoLibrary />}
              onClick={() => setGooglePhotosDialog({ open: true })}
              disabled={uploading || isUploadInProgressElsewhere}
              fullWidth
            >
              Import from Google Photos
            </Button>
          </Box>
        </Grid>

        {/* ── Selected files list ────────────────────────────────────────────────── */}
        <Grid item xs={12} md={6}>
          {previewFiles.length > 0 ? (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Selected ({previewFiles.length})
                </Typography>
                <Button size="small" color="error" onClick={clearAll} disabled={uploading}>
                  Clear All
                </Button>
              </Box>

              <Box sx={{ maxHeight: 360, overflowY: 'auto', pr: 0.5 }}>
                {previewFiles.map((preview, index) => (
                  <Card key={index} sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}>
                    {/* Thumbnail */}
                    {preview.preview ? (
                      <Box
                        component="img"
                        src={preview.preview}
                        sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1, m: 1, flexShrink: 0 }}
                      />
                    ) : (
                      <Box sx={{
                        width: 64, height: 64, borderRadius: 1, m: 1, flexShrink: 0,
                        bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <PhotoIcon sx={{ color: 'grey.500' }} />
                      </Box>
                    )}

                    {/* Meta fields */}
                    <Box sx={{ flex: 1, p: 1, minWidth: 0 }}>
                      <TextField
                        fullWidth size="small" label="Name" value={preview.name}
                        onChange={(e) => updateMeta(index, 'name', e.target.value)}
                        sx={{ mb: 0.5 }}
                      />
                      <TextField
                        fullWidth size="small" label="Description (optional)" value={preview.description}
                        onChange={(e) => updateMeta(index, 'description', e.target.value)}
                        sx={{ mb: 0.5 }}
                      />
                      <TextField
                        fullWidth size="small" label="Tags (comma-separated)" value={preview.tags}
                        onChange={(e) => updateMeta(index, 'tags', e.target.value)}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {formatFileSize(preview.size)}
                        {preview.isHeic && ' · HEIC → JPEG'}
                      </Typography>
                    </Box>

                    <IconButton onClick={() => removeFile(index)} color="error" sx={{ m: 0.5, flexShrink: 0 }}>
                      <Delete />
                    </IconButton>
                  </Card>
                ))}
              </Box>

              {/* Upload progress */}
              {uploading && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Uploading… {uploadProgress}%
                  </Typography>
                  <LinearProgress variant="determinate" value={uploadProgress} />
                </Box>
              )}

              {/* Upload button */}
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<CloudUpload />}
                  onClick={handleUpload}
                  disabled={uploading}
                  fullWidth
                >
                  {uploading ? `Uploading… ${uploadProgress}%` : `Upload ${previewFiles.length} Image${previewFiles.length !== 1 ? 's' : ''}`}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{
              height: '100%', minHeight: 200,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              color: 'text.secondary', textAlign: 'center', p: 3,
            }}>
              <PhotoLibrary sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
              <Typography variant="body2">
                No files selected yet.<br />Drag images into the dropzone or import from Google Photos.
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      {/* ── Import result summary ────────────────────────────────────────────────── */}
      {importResult && (importResult.errors.length > 0 || importResult.skipped.length > 0) && (
        <Box sx={{ mt: 2 }}>
          <Alert
            severity={importResult.errors.length > 0 ? 'warning' : 'info'}
            action={
              <Button size="small" color="inherit" onClick={() => setImportResult(null)}>
                Dismiss
              </Button>
            }
            sx={{ mb: 1 }}
          >
            <Typography variant="body2" fontWeight="bold">
              Import summary: {importResult.uploadedFiles.length} uploaded
              {importResult.skipped.length > 0 && `, ${importResult.skipped.length} skipped (duplicates)`}
              {importResult.errors.length > 0 && `, ${importResult.errors.length} failed`}
            </Typography>
          </Alert>

          {importResult.errors.length > 0 && (
            <Accordion disableGutters>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <ErrorOutline color="error" sx={{ mr: 1 }} />
                <Typography variant="body2" color="error.main" fontWeight="medium">
                  {importResult.errors.length} image{importResult.errors.length !== 1 ? 's' : ''} failed to upload
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <List dense disablePadding>
                  {importResult.errors.map((e, i) => (
                    <ListItem key={i} disablePadding sx={{ py: 0.25 }}>
                      <ListItemText
                        primary={e.item || e.filename || `Image ${i + 1}`}
                        secondary={e.error || 'Unknown error'}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                        secondaryTypographyProps={{ variant: 'caption', color: 'error.main' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          {importResult.skipped.length > 0 && (
            <Accordion disableGutters>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Warning color="warning" sx={{ mr: 1 }} />
                <Typography variant="body2" color="warning.dark" fontWeight="medium">
                  {importResult.skipped.length} image{importResult.skipped.length !== 1 ? 's' : ''} skipped
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <List dense disablePadding>
                  {importResult.skipped.map((s, i) => (
                    <ListItem key={i} disablePadding sx={{ py: 0.25 }}>
                      <ListItemText
                        primary={s.item || s.filename || `Image ${i + 1}`}
                        secondary={s.reason || 'Duplicate'}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                        secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      )}

      {/* ── Google Photos dialog ───────────────────────────────────────────────── */}
      <GooglePhotosDialog
        open={googlePhotosDialog.open}
        onClose={() => setGooglePhotosDialog({ open: false })}
        currentSession={session}
        onPhotosImported={handleGooglePhotosImported}
      />

      {/* ── Session Image Gallery ──────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 3, mb: 2 }}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5, lineHeight: 1, whiteSpace: 'nowrap' }}>Uploaded Photos</Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
      </Box>
      <Box>
        {/* Gallery header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Session Photos
            {galleryTotal > 0 && (
              <Chip
                label={galleryTotal}
                size="small"
                color="primary"
                sx={{ ml: 1, fontWeight: 'bold' }}
              />
            )}
          </Typography>

          {/* Search box */}
          <TextField
            size="small"
            placeholder="Search by name…"
            value={gallerySearch}
            onChange={(e) => handleGallerySearchChange(e.target.value)}
            sx={{ width: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: gallerySearch ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => handleGallerySearchChange('')}
                    edge="end"
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>

        {/* Loading skeleton grid */}
        {galleryLoading && (
          <Grid container spacing={1}>
            {Array.from({ length: galleryRowsPerPage }).map((_, i) => (
              <Grid item key={i} xs={6} sm={4} md={3} lg={2}>
                <Skeleton variant="rectangular" sx={{ aspectRatio: '1 / 1', borderRadius: 1 }} />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Image grid */}
        {!galleryLoading && galleryImages.length > 0 && (
          <Grid container spacing={1}>
            {galleryImages.map((image) => (
              <Grid item key={image.uniqueId} xs={6} sm={4} md={3} lg={2}>
                <Tooltip
                  title={image.name || image.originalName || ''}
                  placement="top"
                  arrow
                >
                  <Paper
                    elevation={1}
                    sx={{
                      position: 'relative',
                      aspectRatio: '1 / 1',
                      overflow: 'hidden',
                      borderRadius: 1,
                      cursor: 'default',
                      '&:hover .img-overlay': { opacity: 1 },
                    }}
                  >
                    <AuthenticatedImage
                      imageId={image.uniqueId}
                      type="thumbnail"
                      fallbackToServe
                      alt={image.name || ''}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />

                    {/* Hover overlay with image name */}
                    <Box
                      className="img-overlay"
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        bgcolor: 'rgba(0,0,0,0.55)',
                        color: '#fff',
                        fontSize: 11,
                        px: 0.75,
                        py: 0.5,
                        opacity: 0,
                        transition: 'opacity 0.15s',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {image.name || image.originalName}
                    </Box>
                  </Paper>
                </Tooltip>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Empty state */}
        {!galleryLoading && galleryImages.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              color: 'text.secondary',
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            {gallerySearch ? (
              <>
                <BrokenImage sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
                <Typography variant="body2">No photos match "{gallerySearch}"</Typography>
              </>
            ) : (
              <>
                <PhotoLibrary sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
                <Typography variant="body2">No photos in this session yet — upload some above.</Typography>
              </>
            )}
          </Box>
        )}

        {/* Pagination */}
        {galleryTotal > 0 && (
          <TablePagination
            component="div"
            count={galleryTotal}
            page={galleryPage}
            onPageChange={(_e, newPage) => setGalleryPage(newPage)}
            rowsPerPage={galleryRowsPerPage}
            onRowsPerPageChange={(e) => {
              setGalleryRowsPerPage(parseInt(e.target.value, 10));
              setGalleryPage(0);
            }}
            rowsPerPageOptions={[12, 24, 48, 96]}
            labelRowsPerPage="Per page:"
            sx={{ mt: 1 }}
          />
        )}
      </Box>

      {/* ── Divider + Continue CTA ─────────────────────────────────────────────── */}
      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          {canContinue ? (
            <Alert severity="success" icon={<CheckCircle />} sx={{ py: 0.5 }}>
              {currentTotal} photo{currentTotal !== 1 ? 's' : ''} ready — click Continue to proceed to face
              recognition.
            </Alert>
          ) : (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Upload at least one photo to continue.
            </Alert>
          )}
        </Box>
        <Button
          variant="contained"
          size="large"
          endIcon={<ArrowForward />}
          onClick={() => onNext({ uploadedCount: totalUploaded })}
          disabled={!canContinue || uploading}
          sx={{ minWidth: 200 }}
        >
          Continue
        </Button>
      </Box>

      {/* ── Snackbar ──────────────────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WizardStep1Upload;
